import { Inject, Injectable, OnDestroy, DOCUMENT } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { merge, Subject, timer } from 'rxjs';
import { filter, map, switchMap, take, takeUntil } from 'rxjs/operators';
import { animate, AnimationBuilder, style } from '@angular/animations';

import { AuthenticatedLoadingCoordinatorService } from '@core/services/infra/authenticated-loading-coordinator.service';

/**
 * Removes `#vex-splash-screen` once the Angular boot curtain has claimed it
 * (overlay revealed), or the boot finished without an overlay (auth/public
 * routes), with a NavigationEnd + 2s fallback so the boot watchdog can still
 * see the node disappear while Angular is alive.
 */
@Injectable({
  providedIn: 'root'
})
export class VexSplashScreenService implements OnDestroy {
  splashScreenElem?: HTMLElement;
  private readonly destroy$ = new Subject<void>();
  private hidden = false;

  constructor(
    private router: Router,
    @Inject(DOCUMENT) private document: Document,
    private animationBuilder: AnimationBuilder,
    private authenticatedLoadingCoordinator: AuthenticatedLoadingCoordinatorService
  ) {
    this.splashScreenElem =
      this.document.body.querySelector('#vex-splash-screen') ?? undefined;

    if (!this.splashScreenElem) {
      return;
    }

    const firstNav$ = this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      take(1)
    );

    const overlayClaim$ = this.authenticatedLoadingCoordinator.overlayClaimingSplash$.pipe(
      filter(claiming => claiming),
      map(() => 'overlay' as const)
    );

    const bootedOnPublicRoute = AuthenticatedLoadingCoordinatorService.isAuthRoute(
      typeof window !== 'undefined' ? window.location.pathname : '/'
    );

    const publicRouteReady$ = this.authenticatedLoadingCoordinator.loadingState$.pipe(
      filter(() => bootedOnPublicRoute),
      filter(state => !state.isLoading || state.phase === 'complete'),
      map(() => 'auth-route' as const)
    );

    // Hot-cache / anti-flash: loading finished without ever revealing Angular chrome.
    const bootComplete$ = this.authenticatedLoadingCoordinator.loadingState$.pipe(
      filter(state => !state.isLoading && state.phase === 'complete'),
      map(() => 'complete' as const)
    );

    const fallback$ = firstNav$.pipe(
      switchMap(() => timer(2000)),
      map(() => 'fallback' as const)
    );

    merge(overlayClaim$, publicRouteReady$, bootComplete$, fallback$)
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe(() => this.hide());
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  hide(): void {
    if (this.hidden || !this.splashScreenElem) {
      return;
    }
    this.hidden = true;

    const player = this.animationBuilder
      .build([
        style({ opacity: 1 }),
        animate(
          '400ms cubic-bezier(0.25, 0.8, 0.25, 1)',
          style({ opacity: 0 })
        )
      ])
      .create(this.splashScreenElem);

    player.onDone(() => this.splashScreenElem?.remove());
    player.play();
  }
}
